// PracticeScreen: main practice flow and script layout (React version)
// TODO: Full conversion — this is a working stub with core structure

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../store';
import { startPractice, stopPractice, pausePractice, resumePractice,
         setLineIndex, setActualIndex, advanceLine, setIsMyTurn,
         addProblemLine, setDrillMode, revealCueLine, clearRevealedCues } from '../store/practiceSlice';
import { getWorkingLines, getCurrentActualIndex, getDisplayLinesCount, getCueMarkup } from './OptionsMenu';
import { startMic, getMicError, MicErrorType, stopMic, initializeLineTracking } from '../services/mic-stt';
import { stopSpeech, speakLine, initializeAudioContext } from '../services/speech';
import { resetLineProgress } from '../store/practiceSlice';
import type { ViewName } from '../app';

interface PracticeScreenProps {
    onNavigate: (view: ViewName) => void;
}

export function PracticeScreen({ onNavigate }: PracticeScreenProps) {
    const dispatch = useAppDispatch();
    const { isPracticing, isPaused, currentLineIndex, isMyTurn,
            isDrillingProblems, problemLineIndices, problemLines,
            revealedCueLineIndices, lineProgress } = useAppSelector(s => s.practice);
    const { myCharacters } = useAppSelector(s => s.script);
    const { removeStageDirections, hideMyLines, cueMode, micEnabled, mode } = useAppSelector(s => s.settings);

    const speechTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lineAdvanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [feedbackHtml, setFeedbackHtml] = useState('');
    const [feedbackClass, setFeedbackClass] = useState('');
    const [showFeedback, setShowFeedback] = useState(false);
    const [micError, setMicError] = useState(getMicError());

    const workingLines = getWorkingLines();
    const myCharSet = new Set(myCharacters);

    // Get display lines (filtered by stage directions)
    const displayLines = isDrillingProblems
        ? problemLineIndices.map(i => ({ line: workingLines[i], actualIndex: i }))
        : workingLines
            .map((line, i) => ({ line, actualIndex: i }))
            .filter(({ line }) => !removeStageDirections || !line.isStageDirection);

    const totalLines = displayLines.length;
    const actualIndex = getCurrentActualIndex();
    const currentLine = actualIndex !== undefined ? workingLines[actualIndex] : null;
    const isMyLine = currentLine ? myCharSet.has(currentLine.character) : false;

    // Begin practice on mount
    useEffect(() => {
        if (!isPracticing) {
            initializeAudioContext(); // Initialize iOS audio
            dispatch(startPractice());
            dispatch(setLineIndex(0));
            if (micEnabled) startMic();
        }
    }, []);

    // Poll for microphone errors
    useEffect(() => {
        if (!micEnabled) return;
        const interval = setInterval(() => {
            setMicError(getMicError());
        }, 1000);
        return () => clearInterval(interval);
    }, [micEnabled]);

    function nextLine() {
        if (!isPracticing || isPaused) return;
        dispatch(advanceLine());
    }

    // Practice flow: speak other lines, auto-advance stage directions, wait on my lines
    useEffect(() => {
        if (!isPracticing || isPaused || isComplete) return;
        if (!currentLine) return;

        const isStage = currentLine.isStageDirection;
        const isMine = myCharSet.has(currentLine.character);

        if (isStage) {
            dispatch(setIsMyTurn(false));
            // Reduced from 2000ms to 1000ms for faster stage direction transitions
            lineAdvanceTimeoutRef.current = setTimeout(() => nextLine(), 1000);
        } else if (isMine) {
            dispatch(setIsMyTurn(true));
            // Initialize line tracking for this line
            initializeLineTracking(currentLine.dialogue);
            if (micEnabled && !isMyTurn) startMic();
            lineAdvanceTimeoutRef.current = setTimeout(() => {
                dispatch(setIsMyTurn(false));
                dispatch(resetLineProgress());
                nextLine();
            }, 12000);
        } else {
            dispatch(setIsMyTurn(false));
            dispatch(resetLineProgress());
            speakLine(currentLine.dialogue, () => {
                // Reduced from 400ms to 150ms for faster transitions after TTS
                lineAdvanceTimeoutRef.current = setTimeout(() => nextLine(), 150);
            }, currentLine.recordedAudioId);
        }

        return () => {
            if (lineAdvanceTimeoutRef.current) {
                clearTimeout(lineAdvanceTimeoutRef.current);
                lineAdvanceTimeoutRef.current = null;
            }
        };
    }, [currentLineIndex, isPracticing, isPaused]);

    function handlePause() {
        if (isPaused) {
            dispatch(resumePractice());
            if (micEnabled) startMic();
        } else {
            dispatch(pausePractice());
            stopSpeech();
            if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
        }
    }

    function handleSkip() {
        if (isPaused) return;
        if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
        if (lineAdvanceTimeoutRef.current) clearTimeout(lineAdvanceTimeoutRef.current);
        stopSpeech();
        dispatch(setIsMyTurn(false));
        setShowFeedback(false);
        nextLine();
    }

    function handleReset() {
        dispatch(stopPractice());
        dispatch(setIsMyTurn(false));
        dispatch(clearRevealedCues());
        stopSpeech();
        if (speechTimeoutRef.current) clearTimeout(speechTimeoutRef.current);
        if (lineAdvanceTimeoutRef.current) clearTimeout(lineAdvanceTimeoutRef.current);
        dispatch(startPractice());
        dispatch(setLineIndex(0));
        if (micEnabled) startMic();
    }

    const percent = totalLines > 0 ? ((currentLineIndex + 1) / totalLines) * 100 : 0;
    const isComplete = currentLineIndex >= totalLines;

    return (
        <div className="practice-container" id="practice-view" style={{ display: 'flex' }}>
            <div className="status-area">
                <div className="status-text">
                    <div className={`status-dot ${
                        micError.type !== MicErrorType.NONE ? 'error'
                        : isComplete ? ''
                        : isMyLine && micEnabled ? 'listening'
                        : !isMyLine && !currentLine?.isStageDirection ? 'speaking'
                        : ''
                    }`}></div>
                    <span>{
                        micError.type !== MicErrorType.NONE ? micError.message
                        : isComplete ? 'Done'
                        : isPaused ? 'Paused'
                        : currentLine?.isStageDirection ? 'Stage direction'
                        : isMyLine && micEnabled ? 'Your line — Listening...'
                        : isMyLine ? 'Your line (mic off)'
                        : `${currentLine?.character || ''} speaking`
                    }</span>
                </div>
                {micError.type !== MicErrorType.NONE &&
                 micError.type !== MicErrorType.PERMISSION_DENIED && (
                    <button
                        className="btn btn-secondary btn-small"
                        onClick={() => { stopMic(); startMic(); }}
                        style={{ marginTop: '8px' }}
                    >
                        Retry Microphone
                    </button>
                )}
            </div>

            <div className="controls">
                <button
                    className="btn btn-large"
                    style={{ background: isPaused ? '#059669' : '#dc2626', color: 'white' }}
                    onClick={handlePause}
                >
                    {isPaused ? '▶ Resume' : '⏸ Pause'}
                </button>
                <button className="btn btn-secondary" onClick={handleSkip}>Skip</button>
                <button className="btn btn-secondary" onClick={handleReset}>Reset</button>
                <button className="btn btn-secondary" onClick={() => onNavigate('character')}>Change Character</button>
            </div>

            <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${percent}%` }}></div>
            </div>
            <p className="progress-text">
                {isDrillingProblems
                    ? `Problem Line ${currentLineIndex + 1} of ${totalLines}`
                    : `Line ${currentLineIndex + 1} of ${totalLines}`}
            </p>

            <div className="practice-script" id="practice-script">
                <div className="practice-script-list">
                    {displayLines.map(({ line, actualIndex: ai }, visibleIndex) => {
                        const isMyCharLine = myCharSet.has(line.character);
                        const roleClass = line.isStageDirection ? 'is-stage' : (isMyCharLine ? 'is-you' : 'is-other');
                        const shouldMask = cueMode && isMyCharLine && !line.isStageDirection;
                        const isActive = ai === actualIndex;
                        const distance = actualIndex !== undefined ? Math.abs(ai - actualIndex) : 999;

                        return (
                            <div
                                key={ai}
                                className={`practice-script-line ${roleClass} ${isActive ? 'is-active' : ''} ${distance <= 2 ? 'is-nearby' : ''} ${shouldMask ? 'is-cueable' : ''} ${revealedCueLineIndices.includes(ai) ? 'is-revealed' : ''} ${hideMyLines && isMyCharLine && !line.isStageDirection ? 'is-concealed' : ''}`}
                                data-actual-index={ai}
                                onClick={() => {
                                    if (shouldMask) dispatch(revealCueLine(ai));
                                }}
                            >
                                <div className="practice-script-speaker">
                                    {line.isStageDirection ? 'Stage direction' : line.character}
                                </div>
                                <div
                                    className="practice-script-dialogue"
                                    dangerouslySetInnerHTML={shouldMask ? { __html: getCueMarkup(line.dialogue, ai) } : undefined}
                                >
                                    {!shouldMask ? line.dialogue : undefined}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="practice-state" aria-hidden="true">
                <div className={`speaker-label ${isComplete ? '' : currentLine?.isStageDirection ? 'stage-direction' : isMyLine ? 'you' : 'other'}`}>
                    {isComplete ? 'COMPLETE' : (currentLine?.isStageDirection ? 'STAGE DIRECTION' : currentLine?.character || '-')}
                </div>
                <div className="dialogue-text">
                    {isComplete
                        ? (isDrillingProblems ? 'Problem lines reviewed! Click Reset to continue.' : 'Great job! You finished the script.')
                        : (currentLine?.dialogue || 'Ready to begin')}
                </div>
                {showFeedback && (
                    <div className={`feedback ${feedbackClass}`} dangerouslySetInnerHTML={{ __html: feedbackHtml }}></div>
                )}
            </div>
        </div>
    );
}
